import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, ChevronDown, MapPin, Plane,
  Car, Snowflake, Check, Star,
  Clock, Route, Lightbulb
} from "lucide-react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const SectionLabel = ({ children }) => (
  <p className="font-heading text-[10px] font-bold uppercase tracking-[0.2em] text-primary mb-2">
    {children}
  </p>
);

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div className={cx(
    "rounded-xl border overflow-hidden transition-all duration-200",
    isOpen ? "border-primary/30 shadow-card" : "border-surface-border"
  )}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-surface hover:bg-surface-raised transition-colors"
      aria-expanded={isOpen}
    >
      <span className="font-heading font-semibold text-text text-sm sm:text-base">{faq.question}</span>
      <ChevronDown size={18} className={cx("flex-shrink-0 text-primary transition-transform duration-200", isOpen && "rotate-180")} />
    </button>
    {isOpen && (
      <div className="px-6 pb-5 pt-1 bg-surface border-t border-surface-border">
        <p className="font-body text-text-muted text-sm leading-relaxed">{faq.answer}</p>
      </div>
    )}
  </div>
);

export default function CountryView({ data }) {
  const [openFaq, setOpenFaq] = useState(null);

  const country = data?.country;
  const seo = data?.seo;
  const ui = data?.ui;

  if (!country) return null;

  return (
    <div className="min-h-screen bg-surface-raised font-body">
      <Helmet>
        <title>{seo?.title || `Car Rental in ${country.name} | RentGurus`}</title>
        {seo?.metaDescription && <meta name="description" content={seo.metaDescription} />}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
        {seo?.structuredData && (
          <script type="application/ld+json">{JSON.stringify(seo.structuredData)}</script>
        )}
      </Helmet>

      {/* ── HERO ── */}
      <div className="relative min-h-[70vh] sm:min-h-[75vh] flex items-center overflow-hidden bg-dark-deep">
        {country.hero?.image && (
          <>
            <img
              src={country.hero.image}
              alt={country.name}
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-deep via-dark-deep/90 to-dark-deep/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-deep via-transparent to-transparent" />
          </>
        )}

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 w-full py-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1.5 rounded-full mb-6">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="font-heading text-[11px] font-semibold text-primary uppercase tracking-widest">
              {country.hero?.badge || "Top Destination"}
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 max-w-2xl">
            {country.hero?.headline?.split(country.name).map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && <span className="text-primary">{country.name}</span>}
              </span>
            )) || country.hero?.headline}
          </h1>

          <p className="font-body text-white/70 text-lg leading-relaxed mb-10 max-w-xl">
            {country.hero?.subheadline}
          </p>

          {/* Quick facts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 max-w-2xl">
            {country.hero?.quickFacts?.map((fact, i) => (
              <div key={i} className="bg-white/10 border border-white/10 rounded-xl p-4">
                <p className="font-heading text-[10px] font-bold uppercase tracking-widest text-primary/80 mb-1">{fact.label}</p>
                <p className="font-heading text-sm font-semibold text-white">{fact.value}</p>
              </div>
            ))}
          </div>

          <a
            href="/"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-primary hover:bg-primary-hover text-white font-heading font-semibold text-sm uppercase tracking-wider transition-colors shadow-primary-glow"
          >
            {ui?.cta?.primaryText || `Search cars in ${country.name}`}
            <ArrowRight size={18} />
          </a>
        </div>
      </div>

      {/* ── POPULAR CITIES ── */}
      {country.popularCities?.length > 0 && (
        <section className="px-4 sm:px-6 py-14">
          <div className="max-w-6xl mx-auto">
            <SectionLabel>Popular Cities</SectionLabel>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-text mb-2">
              Pick up in {country.name}'s major hubs
            </h2>
            <p className="font-body text-text-muted mb-10">Choose your city and find the best daily rates.</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {country.popularCities.map((city) => (
                <a
                  key={city.id}
                  href={`/cities/${country.code?.toLowerCase()}/${city.slug}`}
                  className="group bg-surface rounded-xl border border-surface-border overflow-hidden hover:shadow-card hover:border-primary/30 transition-all duration-300"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={city.image}
                      alt={city.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3 bg-dark-deep/80 backdrop-blur-sm text-white font-heading text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                      From {city.startingPrice?.formatted}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-bold text-text mb-1 group-hover:text-primary transition-colors">
                      {city.name}
                    </h3>
                    <p className="font-body text-sm text-text-muted mb-4">{city.shortDescription}</p>
                    <div className="flex items-center justify-between font-heading text-xs font-semibold text-primary uppercase tracking-wider">
                      <span>View Details</span>
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── MAJOR HUBS + CAR TYPES ── */}
      <section className="px-4 sm:px-6 py-14 bg-surface border-t border-surface-border">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 items-start">

          {/* Airports */}
          <div className="lg:col-span-7">
            <SectionLabel>Pick-up Points</SectionLabel>
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-heading text-3xl font-bold text-text">Major Hubs</h2>
              <p className="font-heading text-xs font-semibold text-primary uppercase tracking-widest hidden sm:block">
                Verified Locations
              </p>
            </div>

            <div className="space-y-3">
              {country.topAirports?.map((airport) => (
                <a
                  key={airport.id}
                  href={`/locations/${airport.slug}`}
                  className="group flex items-center justify-between p-5 bg-surface-raised hover:bg-dark-deep rounded-xl border border-surface-border hover:border-dark-deep transition-all duration-300"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-surface group-hover:bg-primary flex items-center justify-center flex-shrink-0 transition-colors duration-300 shadow-card">
                      <Plane size={20} className="text-text group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-lg text-text group-hover:text-white transition-colors">
                        {airport.iataCode}
                      </h4>
                      <p className="font-body text-sm text-text-muted group-hover:text-white/60 transition-colors">
                        {airport.city} International
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-[10px] font-bold text-text-muted group-hover:text-white/50 uppercase tracking-widest mb-0.5 transition-colors">
                      From
                    </p>
                    <p className="font-heading text-xl font-bold text-primary">
                      {airport.startingPrice?.formatted}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Car types */}
          <div className="lg:col-span-5">
            <div className="bg-dark-deep rounded-xl p-7 h-full">
              <SectionLabel>The Collection</SectionLabel>
              <h2 className="font-heading text-2xl font-bold text-white mb-1">Curated for every terrain</h2>
              <p className="font-body text-white/50 text-sm mb-7">Vehicles matched to {country.name}'s roads.</p>

              <div className="space-y-3">
                {country.carTypes?.map((car) => (
                  <div
                    key={car.id}
                    className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Car size={18} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-white text-sm">{car.name}</h4>
                        <p className="font-body text-white/40 text-xs">{car.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-heading font-bold text-white text-sm">{car.startingPrice?.formatted}</p>
                      <p className="font-body text-[10px] text-white/30 uppercase tracking-wide">/ day</p>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="/"
                className="block w-full mt-6 py-3.5 text-center bg-primary hover:bg-primary-hover text-white font-heading font-semibold text-xs uppercase tracking-widest rounded-xl transition-colors"
              >
                View Full Fleet
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── TOP ROUTES ── */}
      {country.topRoutes?.length > 0 && (
        <section className="px-4 sm:px-6 py-14">
          <div className="max-w-6xl mx-auto">
            <SectionLabel>Scenic Drives</SectionLabel>
            <h2 className="font-heading text-3xl font-bold text-text mb-10">Top Routes</h2>

            <div className="grid sm:grid-cols-2 gap-6">
              {country.topRoutes.map((route) => (
                <div key={route.id} className="group bg-surface rounded-xl border border-surface-border overflow-hidden hover:shadow-card transition-all">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={route.image}
                      alt={route.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      <span className="font-heading text-[10px] font-semibold bg-surface/90 text-text px-3 py-1 rounded-full flex items-center gap-1">
                        <Clock size={10} /> {route.duration}
                      </span>
                      <span className="font-heading text-[10px] font-semibold bg-surface/90 text-text px-3 py-1 rounded-full flex items-center gap-1">
                        <Route size={10} /> {route.distanceKm} km
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-bold text-text text-base mb-1">{route.title}</h3>
                    <p className="font-body text-sm text-text-muted mb-3">{route.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {route.highlights?.map((h) => (
                        <span key={h} className="font-body text-[10px] bg-accent text-accent-foreground px-2 py-1 rounded-md font-medium">
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── VENDORS ── */}
      {country.vendors?.length > 0 && (
        <section className="px-4 sm:px-6 py-14 bg-surface border-t border-surface-border">
          <div className="max-w-6xl mx-auto">
            <SectionLabel>Top Rated Vendors</SectionLabel>
            <h2 className="font-heading text-3xl font-bold text-text mb-10">
              Best vendors in {country.name}
            </h2>

            <div className="grid sm:grid-cols-2 gap-5">
              {country.vendors.map((vendor) => (
                <a
                  key={vendor.id}
                  href={`/vendors/${vendor.slug}`}
                  className="group bg-surface rounded-xl border border-surface-border p-5 hover:border-primary/40 hover:shadow-card transition-all flex items-center gap-5"
                >
                  <div className="w-16 h-16 bg-surface-raised rounded-xl flex items-center justify-center p-3 flex-shrink-0 border border-surface-border">
                    {vendor.logo?.url ? (
                      <img src={vendor.logo.url} alt={vendor.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <Car size={24} className="text-text" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-text text-sm mb-1 group-hover:text-primary transition-colors truncate">
                      {vendor.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Star size={12} className="fill-primary text-primary" />
                      <span className="font-heading text-xs font-semibold text-text">{vendor.rating}</span>
                      <span className="font-body text-xs text-text-muted">({vendor.reviewCount?.toLocaleString()} reviews)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {vendor.tags?.map((tag) => (
                        <span key={tag} className="font-body text-[10px] bg-accent text-accent-foreground px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-heading font-bold text-text text-base">{vendor.startingPrice?.formatted}</p>
                    <p className="font-body text-[10px] text-text-muted uppercase tracking-wide">/ day</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONTENT + WINTER DRIVING ── */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-start">

          <div className="lg:col-span-7 space-y-8">
            {country.contentBlocks?.map((block, i) => (
              <div key={i}>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-4">{block.title}</h2>
                {block.text && <p className="font-body text-text-muted leading-relaxed">{block.text}</p>}
                {block.items && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    {block.items.map((item, j) => (
                      <div key={j} className="bg-surface rounded-xl border border-surface-border p-5 shadow-card">
                        <h4 className="font-heading font-semibold text-text text-sm mb-1">{item.title}</h4>
                        <p className="font-body text-text-muted text-xs leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {country.travelTips?.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-bold text-text mb-5">Travel Tips</h2>
                <div className="space-y-3">
                  {country.travelTips.map((tip, i) => (
                    <div key={i} className="flex gap-4 bg-surface rounded-xl border border-surface-border p-5 shadow-card">
                      <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Lightbulb size={15} className="text-accent-foreground" />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-text text-sm mb-0.5">{tip.title}</h4>
                        <p className="font-body text-text-muted text-xs leading-relaxed">{tip.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Winter driving sidebar */}
          {country.drivingInfo?.winterDriving && (
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="bg-dark-deep rounded-xl p-7 relative overflow-hidden">
                <div className="absolute top-4 right-4 opacity-10">
                  <Snowflake size={80} className="text-white" />
                </div>
                <SectionLabel>Safety First</SectionLabel>
                <h3 className="font-heading text-2xl font-bold text-white mb-2">Winter Driving</h3>
                <p className="font-body text-white/50 text-sm mb-6 leading-relaxed">
                  {country.drivingInfo.winterDriving.note}
                </p>
                <ul className="space-y-3">
                  {country.drivingInfo.winterDriving.tips.map((tip, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Check size={11} className="text-primary" />
                      </div>
                      <span className="font-body text-white/80 text-sm">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── FAQ ── */}
      {country.faqs?.length > 0 && (
        <section className="px-4 sm:px-6 py-14 bg-surface border-t border-surface-border">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <SectionLabel>Support</SectionLabel>
              <h2 className="font-heading text-3xl font-bold text-text mb-2">Frequently Asked Questions</h2>
              <p className="font-body text-text-muted">Everything you need to know about renting a car in {country.name}</p>
            </div>
            <div className="space-y-3">
              {country.faqs.map((faq, i) => (
                <FaqItem
                  key={i}
                  faq={faq}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BOTTOM CTA ── */}
      <section className="px-4 sm:px-6 py-20 bg-dark-deep text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
        <div className="max-w-3xl mx-auto relative z-10">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            Ready to start your <br />
            <span className="text-primary">{country.name} Adventure?</span>
          </h2>
          <p className="font-body text-white/50 mb-10">
            Compare top vendors and book your perfect rental in minutes.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-xl bg-primary hover:bg-primary-hover text-white font-heading font-semibold text-sm uppercase tracking-wider transition-colors shadow-primary-glow"
          >
            {ui?.cta?.primaryText || `Search cars in ${country.name}`}
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}